import {FC} from 'react';
import {Box, FormControl, InputAdornment, RadioGroup, Typography} from '@mui/material';
import {Field, useFormikContext} from 'formik';
import {AuctionFormValues} from '../../formConfiguration';
import {useAppSelector} from '@/store/hooks';
import {getAuctionCategoriesSelector} from '@/store/organization-auction';
import GreyUploadImage from '@/shared/components/UploadDropzone/GreyUploadImage';
import Radio from '@/shared/components/Radio';
import Input from '@/shared/components/Inputs/Input';
import Select from '@/shared/components/Select';
import {setRequiredLabel} from '@/utils/helpers';
import {ReactComponent as DollarIcon} from '@/assets/icons/dollar-circle.svg';
import {
	BODY2,
	CAPTION,
	FALSE,
	FIELD,
	FIELD_LABEL,
	FORM,
	NONE,
	NUMBER,
	PLACEHOLDER,
	PRIMARY,
	START,
	TRUE,
	ZERO,
} from '@/utils/constants';
import {AUCTION_VARIANT} from '@/utils/enums';
import {addItemFormStyle} from './AddItemForm.styles';
import FormHelperText from '@/shared/components/FormHelperText';

const AddItemForm: FC = () => {
	const {
		values: {images, isLiveAuction, description, name, categoryId, value, minBid, bidIncrement, buyNowPrice},
	} = useFormikContext<AuctionFormValues>();

	const currentDescriptionLength = description.length;

	const auctionCategories = useAppSelector(getAuctionCategoriesSelector);

	return (
		<Box sx={addItemFormStyle.wrapper} component={FORM}>
			<Box sx={addItemFormStyle.title}>
				<Typography variant={CAPTION}>Item Photos</Typography>
				<Typography variant={CAPTION}>The first photo will be on the cover of the item.</Typography>
			</Box>

			<Box sx={addItemFormStyle.upload}>
				<Field id={`${FIELD.images}[0]`} name={`${FIELD.images}[0]`} value={images?.[0]} component={GreyUploadImage} />
				<Field id={`${FIELD.images}[1]`} name={`${FIELD.images}[1]`} value={images?.[1]} component={GreyUploadImage} />
				<Field id={`${FIELD.images}[2]`} name={`${FIELD.images}[2]`} value={images?.[2]} component={GreyUploadImage} />
				<Field id={`${FIELD.images}[3]`} name={`${FIELD.images}[3]`} value={images?.[3]} component={GreyUploadImage} />
				<Field id={`${FIELD.images}[4]`} name={`${FIELD.images}[4]`} value={images?.[4]} component={GreyUploadImage} />
				<Field id={`${FIELD.images}[5]`} name={`${FIELD.images}[5]`} value={images?.[5]} component={GreyUploadImage} />
			</Box>

			<Box sx={addItemFormStyle.content}>
				<Box sx={addItemFormStyle.contentItem}>
					<FormControl>
						<RadioGroup value={isLiveAuction}>
							<Box sx={addItemFormStyle.liveAuction}>
								<Field
									id={FIELD.isLiveAuction}
									name={FIELD.isLiveAuction}
									color={PRIMARY}
									value={FALSE}
									component={Radio}
									isBooleanValue
									label={
										<Typography variant={BODY2} sx={addItemFormStyle.radioTitle}>
											{AUCTION_VARIANT.SILENT_AUCTION}
										</Typography>
									}
								/>

								<Field
									id={FIELD.isLiveAuction}
									name={FIELD.isLiveAuction}
									color={PRIMARY}
									value={TRUE}
									isBooleanValue
									component={Radio}
									label={
										<Typography variant={BODY2} sx={addItemFormStyle.radioTitle}>
											{AUCTION_VARIANT.LIVE_AUCTION}
										</Typography>
									}
								/>
							</Box>
						</RadioGroup>
					</FormControl>

					<Box sx={{position: 'relative'}}>
						<Field
							id={FIELD.description}
							name={FIELD.description}
							value={description}
							rows={8}
							sx={addItemFormStyle.description}
							placeholder={PLACEHOLDER.itemDescription}
							label={FIELD_LABEL.itemDescription}
							component={Input}
							multiline
							inputProps={{
								maxLength: 500,
							}}
						/>
						<FormHelperText
							title={`${currentDescriptionLength}/500`}
							sx={{
								display: 'flex',
								justifyContent: 'flex-end',
								mt: 0.5,
								color: 'text.secondary',
								fontSize: '0.75rem',
							}}
						/>
					</Box>
				</Box>

				<Box sx={addItemFormStyle.contentItem}>
					<Field
						id={FIELD.name}
						name={FIELD.name}
						value={name}
						placeholder={PLACEHOLDER.itemName}
						label={setRequiredLabel(FIELD_LABEL.itemName)}
						component={Input}
					/>

					<Field
						id={FIELD.categoryId}
						name={FIELD.categoryId}
						value={categoryId}
						defaultValue={NONE}
						options={auctionCategories}
						label={setRequiredLabel(FIELD_LABEL.category)}
						component={Select}
						parentClassName={addItemFormStyle.categoryValidation}
						sx={addItemFormStyle.category}
					/>

					<Box sx={addItemFormStyle.amountWrapper}>
						<Field
							id={FIELD.value}
							name={FIELD.value}
							value={value}
							label={FIELD_LABEL.value}
							placeholder={String(ZERO)}
							type={NUMBER}
							component={Input}
							InputProps={{
								startAdornment: (
									<InputAdornment position={START}>
										<DollarIcon />
									</InputAdornment>
								),
							}}
							sx={addItemFormStyle.amount}
						/>

						<Field
							id={FIELD.minBid}
							name={FIELD.minBid}
							value={minBid}
							label={setRequiredLabel(FIELD_LABEL.minBid)}
							placeholder={String(ZERO)}
							type={NUMBER}
							component={Input}
							InputProps={{
								startAdornment: (
									<InputAdornment position={START}>
										<DollarIcon />
									</InputAdornment>
								),
							}}
							sx={addItemFormStyle.amount}
						/>
					</Box>

					<Box sx={addItemFormStyle.amountWrapper}>
						<Field
							id={FIELD.bidIncrement}
							name={FIELD.bidIncrement}
							value={bidIncrement}
							label={setRequiredLabel(FIELD_LABEL.bidIncrement)}
							placeholder={String(ZERO)}
							type={NUMBER}
							component={Input}
							InputProps={{
								startAdornment: (
									<InputAdornment position={START}>
										<DollarIcon />
									</InputAdornment>
								),
							}}
							sx={addItemFormStyle.amount}
						/>

						<Field
							id={FIELD.buyNowPrice}
							name={FIELD.buyNowPrice}
							value={buyNowPrice}
							label={FIELD_LABEL.buyNowPrice}
							placeholder={String(ZERO)}
							type={NUMBER}
							component={Input}
							InputProps={{
								startAdornment: (
									<InputAdornment position={START}>
										<DollarIcon />
									</InputAdornment>
								),
							}}
							sx={addItemFormStyle.amount}
						/>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export default AddItemForm;
